import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Scale,
  Receipt,
  Building2,
  Calculator,
  FolderTree,
  Users2,
  Settings,
  X,
  ChevronDown,
  Landmark,
  Calendar,
} from 'lucide-react';
import { Header } from '../components/Header';
import { useAuthStore } from '../store/authStore';

interface NavItem {
  label: string;
  path?: string;
  icon: React.ReactNode;
  children?: { label: string; path: string }[];
  roles?: string[];
}

const navItems: NavItem[] = [
  { label: 'ダッシュボード', path: '/', icon: <LayoutDashboard size={20} /> },
  {
    label: '会計',
    icon: <FileText size={20} />,
    children: [
      { label: '仕訳一覧', path: '/journals' },
      { label: '仕訳入力', path: '/journals/create' },
      { label: '自動仕訳', path: '/journals/auto' },
      { label: '総勘定元帳', path: '/ledger' },
      { label: '試算表', path: '/trial-balance' },
      { label: '決算書作成', path: '/financial-statements' },
    ],
  },
  { label: '証憑管理', path: '/evidence', icon: <Receipt size={20} /> },
  { label: '固定資産', path: '/assets', icon: <Building2 size={20} /> },
  { label: '銀行口座', path: '/bank/accounts', icon: <Landmark size={20} /> },
  {
    label: '税務申告',
    icon: <Calculator size={20} />,
    children: [
      { label: '法人税申告調整', path: '/tax/adjustments' },
      { label: '別表', path: '/tax/schedules' },
      { label: '消費税', path: '/tax/consumption' },
    ],
  },
  {
    label: 'マスタ',
    icon: <FolderTree size={20} />,
    children: [
      { label: '勘定科目', path: '/master/accounts' },
      { label: '部門', path: '/master/departments' },
      { label: '取引先', path: '/master/partners' },
    ],
  },
  { label: 'ユーザー管理', path: '/admin/users', icon: <Users2 size={20} />, roles: ['admin'] },
  { label: '会計期間', path: '/admin/fiscal-periods', icon: <Calendar size={20} />, roles: ['admin'] },
  { label: '設定', path: '/admin/settings', icon: <Settings size={20} />, roles: ['admin'] },
];

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['会計', '税務申告', 'マスタ']);
  const location = useLocation();
  const user = useAuthStore((state) => state.user);

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isChildActive = (children?: { path: string }[]) =>
    children?.some((child) => location.pathname === child.path);

  const filteredNavItems = navItems.filter((item) => {
    // 管理者はすべてのメニューを表示
    if (user?.role === 'admin') return true;
    // rolesが指定されていない場合は全員に表示
    if (!item.roles) return true;
    // rolesが指定されている場合はロールをチェック
    return item.roles.includes(user?.role || '');
  });

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background-main)' }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-[220px] flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:transform-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{
          backgroundColor: 'var(--background-aside-primary)',
          borderRight: 'var(--border-aside)',
        }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <Scale size={18} className="text-white" />
            </div>
            <span className="font-bold text-lg text-white">TOTAROS</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/70 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <div>
                  <button
                    onClick={() => toggleMenu(item.label)}
                    className={`
                      w-full flex items-center justify-between px-4 py-2.5
                      text-sm transition-colors
                      ${isChildActive(item.children) ? 'text-white' : 'text-white/70'}
                    `}
                    style={{
                      backgroundColor: isChildActive(item.children)
                        ? 'var(--background-active)'
                        : 'transparent',
                    }}
                  >
                    <span className="flex items-center gap-3">
                      {item.icon}
                      {item.label}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${
                        expandedMenus.includes(item.label) ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedMenus.includes(item.label) && (
                    <div className="mt-1 ml-4 border-l border-white/20 animate-slideIn">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`
                            block pl-7 pr-4 py-2 text-sm transition-colors
                            ${isActive(child.path) ? 'text-white' : 'text-white/60 hover:text-white'}
                          `}
                          style={{
                            backgroundColor: isActive(child.path)
                              ? 'var(--background-active)'
                              : 'transparent',
                          }}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  to={item.path!}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-2.5
                    text-sm transition-colors
                    ${isActive(item.path!) ? 'text-white' : 'text-white/70 hover:text-white'}
                  `}
                  style={{
                    backgroundColor: isActive(item.path!)
                      ? 'var(--background-active)'
                      : 'transparent',
                  }}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              )}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/50 text-center">
            © 2024 TOTAROS ERP
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

